import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import { useLang } from "@/context/LanguageContext";

export default function Services() {
    const { t } = useLang();
    return (
        <main data-testid="page-services" className="bg-white">
            <SEO pathKey="services" path="/services" />
            <section className="pt-28 lg:pt-36 pb-16 lg:pb-24 blueprint-bg">
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                    <div className="lg:col-span-8">
                        <Reveal>
                            <div className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-steel-600">
                                {t.services.kicker}
                            </div>
                            <h1 className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter text-navy leading-[0.92]">
                                {t.services.h1}
                            </h1>
                        </Reveal>
                    </div>
                    <Reveal delay={120} className="lg:col-span-4">
                        <p className="text-steel-600 leading-relaxed">{t.services.intro}</p>
                    </Reveal>
                </div>
            </section>

            <section className="py-12 lg:py-20">
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10 space-y-1">
                    {t.services.items.map((it, idx) => (
                        <Reveal
                            key={it.code}
                            delay={idx * 60}
                            className="group grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 border-t border-steel-100 py-10 lg:py-14 hover:bg-steel-50 transition-colors"
                            data-testid={`service-row-${idx}`}
                        >
                            <div className="lg:col-span-3">
                                <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-steel-600">
                                    {it.code}
                                </div>
                                <div className="font-display text-6xl lg:text-7xl text-navy/15 mt-3">
                                    {String(idx + 1).padStart(2, "0")}
                                </div>
                            </div>
                            <div className="lg:col-span-5">
                                <h3 className="font-display text-3xl lg:text-4xl uppercase tracking-tighter text-navy leading-[0.95]">
                                    {it.title}
                                </h3>
                                <p className="mt-5 text-steel-600 leading-relaxed">{it.desc}</p>
                            </div>
                            <div className="lg:col-span-4">
                                <ul className="space-y-3">
                                    {it.bullets.map((b) => (
                                        <li key={b} className="flex items-start gap-3 text-navy">
                                            <span className="mt-1.5 inline-block w-4 h-px bg-navy" />
                                            <span className="leading-relaxed">{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                    ))}
                    <div className="border-t border-steel-100" />
                </div>
            </section>

            <section className="bg-navy text-white py-24 lg:py-32 relative grain overflow-hidden">
                <div className="absolute inset-0 blueprint-bg opacity-10" />
                <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
                    <Reveal>
                        <h2 className="font-display text-4xl lg:text-6xl uppercase tracking-tighter leading-[0.95] max-w-3xl">
                            {t.services.process.title}
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-s border-white/10">
                        {t.services.process.steps.map((s, i) => (
                            <Reveal
                                key={s.n}
                                delay={i * 80}
                                className="p-8 border-e border-b border-white/10"
                            >
                                <div className="font-display text-4xl text-white/30">{s.n}</div>
                                <div className="mt-6 font-display text-2xl uppercase tracking-tight">
                                    {s.t}
                                </div>
                                <p className="mt-3 text-steel-200 leading-relaxed text-sm">{s.b}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20 border-t border-steel-100">
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="font-display text-3xl lg:text-4xl text-navy uppercase tracking-tighter leading-tight max-w-2xl">
                        {t.home.cta.title}
                    </div>
                    <Link
                        to="/contact"
                        data-testid="services-cta"
                        className="inline-flex items-center gap-3 bg-navy text-white px-7 py-4 font-mono-tech text-[12px] uppercase tracking-[0.2em] hover:bg-navy-light transition-colors"
                    >
                        {t.common.discuss}
                        <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
                    </Link>
                </div>
            </section>
        </main>
    );
}
